// Visual language follows the other admin pages (Leave Management, Employee
// Actions): a glass header card tucked under the topbar, a row of shared
// Mo3tiStatCards, a "Search & Filter" card and a content card — all with the
// same glass surface, #2e7cba brand accent and SearchBar/ReusableDropdown inputs.
export const BRAND = "#2e7cba";
export const BRAND_DARK = "#256a9f";
export const DANGER = "#ef4444";

const getDepartmentsStyles = (screenSize, theme, language) => {
  const { isMobile, isTablet } = screenSize;
  const isRTL = language === "ar";
  const dark = theme === "dark";

  const c = {
    text: dark ? "#ffffff" : "#0f172a",
    body: dark ? "rgba(255, 255, 255, 0.85)" : "#334155",
    muted: dark ? "rgba(255, 255, 255, 0.6)" : "#64748b",
    border: dark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
    rowBorder: dark ? "rgba(255, 255, 255, 0.06)" : "#f1f5f9",
    inputBg: dark ? "rgba(15, 23, 42, 0.5)" : "rgba(248, 250, 252, 0.8)",
    inputBorder: "rgba(148, 163, 184, 0.3)",
    softBg: dark ? "rgba(255, 255, 255, 0.04)" : "#f8fafc",
    chipBg: dark ? "rgba(46, 124, 186, 0.2)" : "rgba(46, 124, 186, 0.08)",
    chipText: dark ? "#8cc4ee" : BRAND,
  };

  const input = {
    padding: "11px 14px",
    borderRadius: "12px",
    border: `1px solid ${c.inputBorder}`,
    background: c.inputBg,
    color: c.text,
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    minWidth: 0,
  };

  // Same glass surface as Mo3tiStatCard / the Leave Management cards.
  const glass = {
    background: dark ? "rgba(255, 255, 255, 0.02)" : "rgba(255, 255, 255, 0.9)",
    border: dark ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid rgba(0, 0, 0, 0.05)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    boxShadow: dark
      ? "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
      : "0 8px 32px rgba(0, 0, 0, 0.08)",
  };

  const btnBase = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: isMobile ? "11px 16px" : "12px 20px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 700,
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    border: "1px solid transparent",
    transition: "all 0.2s ease",
    outline: "none",
  };

  return {
    colors: c,

    container: {
      padding: isMobile ? "0 12px" : "0",
      direction: isRTL ? "rtl" : "ltr",
      color: c.text,
    },

    // Generic content card (also used by the loading skeleton).
    card: {
      ...glass,
      borderRadius: "16px",
      padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
      marginBottom: "24px",
    },

    // ── header card (same as Leave Management) ──
    header: {
      ...glass,
      borderRadius: isMobile ? "16px" : "24px",
      padding: isMobile ? "20px" : "32px",
      marginTop: "-36px",
      marginBottom: "22px",
      display: "flex",
      alignItems: isMobile ? "stretch" : "flex-start",
      justifyContent: "space-between",
      flexDirection: isMobile ? "column" : "row",
      flexWrap: "wrap",
      gap: isMobile ? "18px" : "24px",
      animation: "deptSlideDown 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
    },
    headerText: { minWidth: 0, flex: 1 },
    title: {
      fontSize: isMobile ? "22px" : "28px",
      fontWeight: 700,
      letterSpacing: "-0.5px",
      color: dark ? "#f1f5f9" : "#1e293b",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    titleIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    subtitle: {
      fontSize: isMobile ? "14px" : "15px",
      fontWeight: 500,
      color: dark ? "#cbd5e1" : "#64748b",
      margin: "12px 0 0 0",
      lineHeight: 1.6,
    },
    headerActions: {
      display: "flex",
      gap: "12px",
      flexWrap: "wrap",
      alignItems: "center",
      flexShrink: 0,
    },

    // ── stats ──
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))",
      gap: "clamp(16px, 3vw, 24px)",
      marginBottom: "24px",
    },

    // ── "Search & Filter" card (same as Leave Management's FiltersBar) ──
    sectionCard: {
      ...glass,
      borderRadius: "16px",
      padding: isMobile ? "18px" : "24px",
      marginBottom: "24px",
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "12px",
      marginBottom: isMobile ? "16px" : "22px",
      paddingBottom: isMobile ? "12px" : "16px",
      borderBottom: `1px solid ${dark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
    },
    sectionTitle: {
      fontSize: isMobile ? "16px" : "19px",
      fontWeight: 700,
      color: c.text,
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    sectionSubtitle: { fontSize: "13.5px", color: c.muted, margin: "4px 0 0 0" },
    sectionMeta: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "13.5px",
      fontWeight: 500,
      color: c.muted,
    },
    toolbar: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)",
      gap: isMobile ? "14px" : "20px",
      alignItems: "end",
    },
    filterGroup: { display: "flex", flexDirection: "column", gap: "8px", minWidth: 0 },
    filterLabel: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "13px",
      fontWeight: 700,
      color: dark ? "rgba(255, 255, 255, 0.9)" : "#374151",
      letterSpacing: isRTL ? 0 : "0.3px",
      textTransform: isRTL ? "none" : "uppercase",
    },
    input,
    select: { ...input, width: "100%", cursor: "pointer" },
    checkboxLabel: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px",
      fontWeight: 500,
      color: c.body,
      cursor: "pointer",
      whiteSpace: "nowrap",
      userSelect: "none",
    },
    checkbox: { width: "16px", height: "16px", margin: 0, accentColor: BRAND, cursor: "pointer", flexShrink: 0 },

    resultsBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "8px",
      marginBottom: "16px",
      fontSize: "13.5px",
      color: c.muted,
    },

    // ── buttons ──
    btnPrimary: (disabled = false) => ({
      ...btnBase,
      background: BRAND,
      color: "#ffffff",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.55 : 1,
    }),
    btnSecondary: (disabled = false) => ({
      ...btnBase,
      background: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.05)",
      color: c.body,
      borderColor: c.border,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.55 : 1,
    }),
    btnDanger: (disabled = false) => ({
      ...btnBase,
      background: DANGER,
      color: "#ffffff",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.55 : 1,
    }),
    iconBtn: (tone = "default") => ({
      width: "34px",
      height: "34px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "8px",
      border: "none",
      cursor: "pointer",
      flexShrink: 0,
      transition: "background 0.2s ease, color 0.2s ease",
      background: "transparent",
      color: tone === "danger" ? DANGER : c.muted,
    }),

    // ── table ──
    tableWrap: {
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      border: `1px solid ${c.border}`,
      borderRadius: "14px",
    },
    table: { width: "100%", minWidth: "820px", borderCollapse: "collapse", fontSize: "14px" },
    th: {
      textAlign: "start",
      padding: "14px 18px",
      fontSize: "12px",
      fontWeight: 700,
      letterSpacing: isRTL ? 0 : "0.04em",
      textTransform: isRTL ? "none" : "uppercase",
      color: c.muted,
      background: c.softBg,
      borderBottom: `1px solid ${c.border}`,
      whiteSpace: "nowrap",
    },
    td: {
      padding: "16px 18px",
      borderBottom: `1px solid ${c.rowBorder}`,
      verticalAlign: "middle",
      color: c.body,
      textAlign: "start",
    },
    deptCell: { display: "flex", alignItems: "center", gap: "12px", minWidth: 0 },
    deptIcon: (active = true) => ({
      width: "40px",
      height: "40px",
      borderRadius: "12px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: active ? c.chipBg : (dark ? "rgba(148, 163, 184, 0.14)" : "rgba(100, 116, 139, 0.1)"),
      color: active ? c.chipText : c.muted,
    }),
    deptName: { fontWeight: 700, fontSize: "15px", color: c.text, lineHeight: 1.4 },
    deptAltName: { fontSize: "12.5px", color: c.muted, marginTop: "2px", lineHeight: 1.4 },
    chipList: { display: "flex", flexWrap: "wrap", gap: "6px" },
    chip: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "4px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 600,
      lineHeight: 1.5,
      background: c.chipBg,
      color: c.chipText,
      border: "none",
      maxWidth: "220px",
      fontFamily: "inherit",
    },
    chipText: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    statusChip: (active) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "4px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 700,
      whiteSpace: "nowrap",
      background: active
        ? (dark ? "rgba(16, 185, 129, 0.18)" : "rgba(16, 185, 129, 0.1)")
        : (dark ? "rgba(148, 163, 184, 0.18)" : "rgba(100, 116, 139, 0.1)"),
      color: active ? "#10b981" : c.muted,
    }),
    statusDot: (active) => ({
      width: "6px", height: "6px", borderRadius: "50%",
      background: active ? "#10b981" : c.muted,
    }),
    mutedText: { fontSize: "13px", color: c.muted },
    companyText: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "13px",
      fontWeight: 600,
      color: c.body,
    },
    warnChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "4px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 600,
      background: dark ? "rgba(245, 158, 11, 0.14)" : "rgba(245, 158, 11, 0.1)",
      color: dark ? "#fbbf24" : "#b45309",
      whiteSpace: "nowrap",
    },

    // ── mobile cards ──
    mobileList: { display: "flex", flexDirection: "column", gap: "12px" },
    mobileCard: {
      border: `1px solid ${c.border}`,
      borderRadius: "14px",
      padding: "14px",
      background: dark ? "rgba(255, 255, 255, 0.02)" : "#ffffff",
    },
    mobileCardTop: { display: "flex", alignItems: "flex-start", gap: "12px" },
    mobileCardMeta: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "8px",
      marginTop: "12px",
    },
    mobileCardActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "4px",
      marginTop: "10px",
      paddingTop: "10px",
      borderTop: `1px solid ${c.rowBorder}`,
    },
    actionsCell: { display: "flex", gap: "6px", alignItems: "center" },
    // Filled pill buttons — same look as the Payroll / Attendance / Employee
    // tables (btnStyle there).
    actionBtn: (variant = "neutral") => ({
      padding: "8px 12px",
      borderRadius: "8px",
      border: "none",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      fontSize: "12px",
      fontWeight: 600,
      fontFamily: "inherit",
      transition: "all 0.2s ease",
      flexShrink: 0,
      background: variant === "danger"
        ? (dark ? "rgba(239,68,68,0.25)" : "rgba(254,226,226,0.9)")
        : (dark ? "rgba(71,85,105,0.6)" : "rgba(226,232,240,0.8)"),
      color: variant === "danger"
        ? (dark ? "#fca5a5" : "#b91c1c")
        : (dark ? "#f1f5f9" : "#334155"),
    }),

    emptyState: {
      textAlign: "center",
      padding: isMobile ? "40px 16px" : "56px 20px",
      color: c.muted,
    },
    emptyIcon: { opacity: 0.35, marginBottom: "12px" },
    emptyTitle: { fontSize: "16px", fontWeight: 600, color: c.body, margin: "0 0 4px 0" },

    // ── modals ──
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.6)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: isMobile ? "16px" : "20px",
      direction: isRTL ? "rtl" : "ltr",
      animation: "deptFadeIn 0.2s ease",
    },
    modal: (maxWidth = 520) => ({
      background: dark ? "rgba(30, 41, 59, 0.98)" : "#ffffff",
      color: c.text,
      borderRadius: isMobile ? "16px" : "20px",
      width: "100%",
      maxWidth: isMobile ? "100%" : `${maxWidth}px`,
      maxHeight: "85vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      border: dark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.08)",
      boxShadow: dark ? "0 20px 60px rgba(0, 0, 0, 0.6)" : "0 20px 60px rgba(0, 0, 0, 0.2)",
      animation: "deptSlideIn 0.25s ease",
    }),
    modalHeader: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: "12px",
      padding: isMobile ? "18px 18px 14px" : "22px 24px 16px",
      borderBottom: `1px solid ${c.border}`,
    },
    modalTitle: {
      fontSize: isMobile ? "17px" : "19px",
      fontWeight: 700,
      margin: 0,
      lineHeight: 1.4,
      minWidth: 0,
      overflowWrap: "anywhere",
    },
    modalSubtitle: { fontSize: "13px", color: c.muted, margin: "4px 0 0 0", overflowWrap: "anywhere" },
    modalBody: { padding: isMobile ? "18px" : "24px 28px", overflowY: "auto", flex: 1 },
    modalFooter: {
      display: "flex",
      gap: "10px",
      justifyContent: "flex-end",
      flexWrap: "wrap",
      padding: isMobile ? "14px 18px" : "16px 24px",
      borderTop: `1px solid ${c.border}`,
      background: c.softBg,
    },
    field: { marginBottom: "16px" },
    label: { display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px", color: c.body },
    fieldInput: { ...input, width: "100%" },
    hint: { fontSize: "13px", color: c.muted, lineHeight: 1.7, margin: 0 },
    notice: (tone = "info") => ({
      display: "flex",
      gap: "10px",
      alignItems: "flex-start",
      padding: "12px 14px",
      borderRadius: "10px",
      fontSize: "13px",
      lineHeight: 1.7,
      background: tone === "warning"
        ? (dark ? "rgba(245, 158, 11, 0.12)" : "rgba(245, 158, 11, 0.08)")
        : c.chipBg,
      color: tone === "warning" ? (dark ? "#fbbf24" : "#b45309") : c.chipText,
      border: `1px solid ${tone === "warning" ? "rgba(245, 158, 11, 0.3)" : "rgba(46, 124, 186, 0.25)"}`,
    }),
    managerRow: {
      display: "grid",
      gridTemplateColumns: isMobile ? "minmax(0, 1fr) 104px auto" : "minmax(0, 1fr) 130px auto",
      gap: "8px",
      alignItems: "center",
      marginBottom: "10px",
    },
    suggestionCard: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "10px",
      padding: "12px 14px",
      borderRadius: "12px",
      marginBottom: "16px",
      background: c.chipBg,
      border: "1px solid rgba(46, 124, 186, 0.25)",
    },
    // ── managers modal ──
    managersHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "10px",
      marginBottom: "12px",
      fontSize: "13px",
      fontWeight: 700,
      color: c.body,
      textTransform: isRTL ? "none" : "uppercase",
      letterSpacing: isRTL ? 0 : "0.04em",
    },
    managersEmpty: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "8px",
      textAlign: "center",
      padding: "28px 16px",
      borderRadius: "14px",
      border: `1px dashed ${c.border}`,
      background: c.softBg,
      color: c.muted,
      marginBottom: "12px",
    },
    managerCard: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "12px",
      padding: "12px 14px",
      borderRadius: "14px",
      border: `1px solid ${c.border}`,
      background: dark ? "rgba(255, 255, 255, 0.02)" : "#ffffff",
    },
    managerAvatar: (isManager = true) => ({
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      fontSize: "14px",
      fontWeight: 700,
      background: isManager ? BRAND : (dark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0"),
      color: isManager ? "#ffffff" : c.body,
    }),
    roleToggle: {
      display: "inline-flex",
      padding: "3px",
      gap: "2px",
      borderRadius: "10px",
      background: dark ? "rgba(15, 23, 42, 0.6)" : "#f1f5f9",
      border: `1px solid ${c.border}`,
      flexShrink: 0,
    },
    addRowBtn: {
      width: "100%",
      marginTop: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      padding: "12px",
      borderRadius: "12px",
      border: `1.5px dashed ${dark ? "rgba(140, 196, 238, 0.4)" : "rgba(46, 124, 186, 0.45)"}`,
      background: "transparent",
      color: c.chipText,
      fontSize: "14px",
      fontWeight: 700,
      fontFamily: "inherit",
      cursor: "pointer",
      transition: "background 0.15s ease",
    },

    memberRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      padding: "10px 0",
      borderBottom: `1px solid ${c.rowBorder}`,
    },
    memberCode: {
      fontSize: "12px",
      fontWeight: 600,
      color: c.muted,
      direction: "ltr",
      unicodeBidi: "isolate",
      whiteSpace: "nowrap",
    },
  };
};

export const getDepartmentsGlobalCss = (theme) => `
  .dept-row:hover td { background: ${theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(46,124,186,0.03)"}; }
  .dept-row:last-child td { border-bottom: none; }
  .dept-icon-btn:hover:not(:disabled) { background: ${theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)"}; color: ${theme === "dark" ? "#ffffff" : "#0f172a"}; }
  .dept-icon-btn.danger:hover:not(:disabled) { background: rgba(239,68,68,0.12); color: #ef4444; }
  .dept-btn-primary:hover:not(:disabled) { background: ${BRAND_DARK} !important; }
  .dept-chip-btn:hover { filter: brightness(${theme === "dark" ? 1.2 : 0.95}); }
  .dept-input:focus { border-color: rgba(46,124,186,0.6) !important; box-shadow: 0 0 0 3px rgba(46,124,186,0.12); }
  .dept-input::placeholder { color: ${theme === "dark" ? "rgba(203,213,225,0.5)" : "rgba(100,116,139,0.6)"}; }
  .dept-input option { background: ${theme === "dark" ? "#1e293b" : "#ffffff"}; color: ${theme === "dark" ? "#f1f5f9" : "#0f172a"}; }
  .dept-scope *:focus-visible { outline: 2px solid ${BRAND}; outline-offset: 2px; }
  .dept-action-btn:hover { transform: translateY(-2px); background: ${theme === "dark" ? "rgba(71,85,105,0.8)" : "rgba(203,213,225,0.8)"} !important; }
  .dept-action-btn.danger:hover { background: ${theme === "dark" ? "rgba(239,68,68,0.4)" : "rgba(254,202,202,0.9)"} !important; }
  .dept-check {
    appearance: none;
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    margin: 0;
    flex-shrink: 0;
    border-radius: 50%;
    border: 2px solid ${theme === "dark" ? "rgba(148,163,184,0.55)" : "#cbd5e1"};
    background: ${theme === "dark" ? "rgba(15,23,42,0.6)" : "#ffffff"};
    display: inline-grid;
    place-content: center;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
    vertical-align: middle;
  }
  .dept-check:hover:not(:disabled) { border-color: ${BRAND}; }
  .dept-check:checked {
    background: ${BRAND} url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E\") center / 12px 12px no-repeat;
    border-color: ${BRAND};
  }
  .dept-check:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(46,124,186,0.3); }
  .dept-check:disabled { opacity: 0.4; }
  .dept-add-row:hover { background: ${theme === "dark" ? "rgba(46,124,186,0.12)" : "rgba(46,124,186,0.06)"} !important; }
  .dept-mobile-card:hover { border-color: rgba(46,124,186,0.35) !important; }
  @keyframes deptFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes deptSlideDown { from { opacity: 0; transform: translateY(-16px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes deptSlideIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
  @media (prefers-reduced-motion: reduce) {
    .dept-scope *, .dept-modal-overlay { animation: none !important; transition: none !important; }
  }
`;

export default getDepartmentsStyles;
